import { openDB } from 'idb';
import { DATA_VERSION } from './version';

// An IndexedDB open request that gets blocked never settles: it fires neither
// success nor error, so `await openDB(...)` hangs forever and callers relying
// on a rejection to fall back to the network stall silently. Blocking happens
// whenever another tab holds the database open at an older version.
export const DB_OPEN_TIMEOUT = 5000;

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team', 'canned_response'];
    this.accountId = accountId;
    this.db = null;
  }

  async initDb() {
    if (this.db) return this.db;

    // Without an account id the name collapses to `cw-store-`, an orphan
    // database that no reader ever queries. Fail loudly so the caller falls
    // back to the network instead of caching into a store nobody reads.
    if (!this.accountId) {
      throw new Error('DataManager: cannot open the cache without an account');
    }

    const dbName = `cw-store-${this.accountId}`;

    let onBlocked;
    let timeoutId;
    const givesUp = new Promise((_, reject) => {
      onBlocked = () =>
        reject(new Error(`IndexedDB open blocked for ${dbName}`));
      timeoutId = setTimeout(
        () => reject(new Error(`IndexedDB open timed out for ${dbName}`)),
        DB_OPEN_TIMEOUT
      );
    });

    try {
      this.db = await Promise.race([
        openDB(dbName, DATA_VERSION, {
          upgrade(db) {
            // Existing databases already carry the stores added in earlier
            // versions, and createObjectStore throws on a name already taken.
            const createStore = (name, options) => {
              if (db.objectStoreNames.contains(name)) return;
              db.createObjectStore(name, options);
            };

            createStore('cache-keys');
            createStore('inbox', { keyPath: 'id' });
            createStore('label', { keyPath: 'id' });
            createStore('team', { keyPath: 'id' });
            createStore('canned_response', { keyPath: 'id' });
          },
          blocked: () => onBlocked(),
          // Another tab is upgrading. Release our handle, otherwise we are the
          // ones blocking it and both tabs wait on each other.
          blocking: () => this.closeDb(),
        }),
        givesUp,
      ]);
    } finally {
      clearTimeout(timeoutId);
    }

    // Store the database name in LocalStorage
    const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
    if (!dbNames.includes(dbName)) {
      dbNames.push(dbName);
      localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
    }

    return this.db;
  }

  closeDb() {
    this.db?.close();
    this.db = null;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    await this.db.clear(modelName);
    return this.push({ modelName, data });
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  // Patches a single cached record in place, without touching the cache key, so it
  // does not trigger a full refetch on the next cold load.
  async update({ modelName, id, data }) {
    this.validateModel(modelName);
    const record = await this.db.get(modelName, Number(id));
    if (!record) return;
    await this.db.put(modelName, { ...record, ...data });
  }

  async setCacheKeys(cacheKeys) {
    Object.keys(cacheKeys).forEach(async modelName => {
      this.db.put('cache-keys', cacheKeys[modelName], modelName);
    });
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
